import type React from "react";

import { cn } from "@/lib/utils/index";

import {
  SKELETON_PULSE_CLASS,
  SKELETON_SHAPE_CLASS,
  SKELETON_TONE_CLASS,
  type SkeletonShape,
  type SkeletonTone,
} from "./_logic/skeleton.constants";

interface FetchSkeletonProps {
  /** What the block stands in for; sets its corner radius. Defaults to `block`. */
  shape?: SkeletonShape;
  /** `strong` (gray-200) for a name or title line, `base` (gray-100) for everything else. Defaults to `base`. */
  tone?: SkeletonTone;
  /** Width as a CSS length or a pixel number. Prefer a Tailwind width in `className` when one fits. */
  width?: number | string;
  /** Height as a CSS length or a pixel number. Prefer a Tailwind height in `className` when one fits. */
  height?: number | string;
  /** Size and layout classes, e.g. `h-9 w-40` or `size-7 shrink-0`. */
  className?: string;
}

/**
 * One pulsing placeholder block: the base every `FetchSkeleton*` primitive is built from.
 *
 * Shape only. It never holds text, a spinner, or a "Loading" label, and it is `aria-hidden`:
 * the screen-reader announcement belongs to the one `FetchSkeletonRegion` that wraps the page's
 * skeleton. The pulse stops for viewers who asked for reduced motion.
 *
 * @param props - See {@link FetchSkeletonProps}.
 * @returns The placeholder block.
 * @throws Never.
 */
export function FetchSkeleton({
  shape = "block",
  tone = "base",
  width,
  height,
  className,
}: FetchSkeletonProps): React.ReactElement {
  // Derived values
  const style = width === undefined && height === undefined ? undefined : { width, height };

  // Render
  return (
    <div
      aria-hidden="true"
      data-skeleton-block=""
      style={style}
      className={cn(SKELETON_PULSE_CLASS, SKELETON_TONE_CLASS[tone], SKELETON_SHAPE_CLASS[shape], className)}
    />
  );
}
